import { useEffect, useRef } from 'react';
import { FlattenInterpolation, ThemeProps } from 'styled-components';
import {
  useModelForm, FormInput, MultiSelectInput, ThemeType,
  useIsAutoSave,
} from '../../../../..';

export function ModelMultiSelect({
  id = '',
  options = {},
  field,
  label = '',
  labelStyles = '',
  disabled = false,
  touchOverride = false,
  valueOverride,
  errorOverride = '',
  mutateValue = (newValue) => newValue,
  onVisibleChange,
  containerCss,
  required = false,
  inputContainerStyles,
  isTooltipError = false,
  placeholder = '',
}: {
  id?: string,
  options?: { [key: string]: string },
  field: string,
  label?: string,
  labelStyles?: string,
  disabled?: boolean,
  touchOverride?: boolean,
  valueOverride?: string[],
  errorOverride?: string,
  mutateValue?: (newValue: string[]) => any,
  onVisibleChange?: (visible: boolean) => void,
  containerCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  required?: boolean,
  inputContainerStyles?: string,
  isTooltipError?: boolean,
  placeholder?: string;
}) {
  const {
    visible, errors, value, update, warnings,
  } = useModelForm(field, valueOverride, errorOverride);

  const { isAutoSave } = useIsAutoSave();

  const changed = useRef<boolean>(false);

  useEffect(() => {
    onVisibleChange?.(visible);
  }, [visible]);

  const onDeselectOption = (option: string) => {
    update({
      [field]: mutateValue([...(value.filter((v: string) => v !== option))]),
    });
  };

  const onMenuClose = (newValue: string[]) => {
    if (changed.current && isAutoSave) {
      update({ [field]: mutateValue(newValue) });
    }
    changed.current = false;
  };

  const onChange = (newValue: string[]) => {
    if (newValue?.length === 0) {
      return update({ [field]: mutateValue(newValue) });
    }

    changed.current = true;
    return update(
      { [field]: mutateValue(newValue) },
      isAutoSave,
    );
  };

  return (
    <FormInput visible={visible} css={containerCss}>
      <MultiSelectInput
        options={options}
        id={`${id}-${field}`}
        label={label}
        labelStyles={labelStyles}
        value={value !== '' ? value : undefined}
        errors={errors}
        warnings={warnings}
        touchOverride={touchOverride}
        onChange={onChange}
        disabled={disabled}
        onDeselectOption={onDeselectOption}
        required={required}
        containerStyles={inputContainerStyles}
        onMenuClose={onMenuClose}
        isTooltipError={isTooltipError}
        placeholder={placeholder}
      />
    </FormInput>
  );
}
